'use client';
import React, { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

interface QRLightboxProps {
  isOpen: boolean;
  type: 'whatsapp' | 'facebook' | null;
  onClose: () => void;
  language: 'en' | 'ar';
}

export default function QRLightbox({ isOpen, type, onClose, language }: QRLightboxProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !type) return null;

  const config = {
    whatsapp: {
      src: '/assets/images/WhatsApp_QR_Code-1788029252557.jpeg',
      alt: 'Scan to chat with PLC-IRAQ on WhatsApp',
      labelEn: 'Scan to Chat on WhatsApp',
      labelAr: 'امسح للدردشة عبر واتسآب',
      descEn: 'Open your camera app and point it at the QR code to start a WhatsApp conversation with our team.',
      descAr: 'افتح تطبيق الكاميرا ووجّهه نحو رمز QR لبدء محادثة واتسآب مع فريقنا.',
      color: 'text-iraqi-green',
      bgColor: 'bg-iraqi-green/10',
      borderColor: 'border-iraqi-green/30',
    },
    facebook: {
      src: '/assets/images/Facebook_QR_Code-1788029252586.jpeg',
      alt: 'Follow PLC-IRAQ on Facebook',
      labelEn: 'Follow Us on Facebook',
      labelAr: 'تابعنا على فيسبوك',
      descEn: 'Scan this QR code to follow PLC-IRAQ on Facebook and stay updated with our latest products and news.',
      descAr: 'امسح رمز QR هذا لمتابعة PLC-IRAQ على فيسبوك والبقاء على اطلاع بآخر الأخبار.',
      color: 'text-blue-400',
      bgColor: 'bg-blue-400/10',
      borderColor: 'border-blue-400/30',
    },
  };

  const current = config[type];

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={language === 'ar' ? current.labelAr : current.labelEn}
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Lightbox Card */}
      <div className="relative bg-onyx border border-white/10 rounded-2xl p-8 max-w-sm w-full text-center animate-slide-up">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-200"
          aria-label="Close QR lightbox"
        >
          <X size={18} />
        </button>

        {/* Label */}
        <h3 className={`text-lg font-bold font-space-grotesk mb-2 ${current.color}`}>
          {language === 'ar' ? current.labelAr : current.labelEn}
        </h3>
        <p className="text-sm text-slate-400 font-roboto mb-6 leading-relaxed">
          {language === 'ar' ? current.descAr : current.descEn}
        </p>

        {/* QR Code */}
        <div className={`inline-block p-4 bg-white rounded-2xl border-2 ${current.borderColor} shadow-xl`}>
          <img
            src={current.src}
            alt={current.alt}
            className="w-72 h-72 object-contain"
            style={{ width: '288px', height: '288px' }}
          />
        </div>

        {/* Platform label */}
        <div className={`mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full ${current.bgColor} border ${current.borderColor}`}>
          <span className={`text-sm font-semibold font-space-grotesk ${current.color}`}>
            {type === 'whatsapp' ? 'WhatsApp' : 'Facebook'}
          </span>
        </div>
      </div>
    </div>
  );
}
